import { Entity } from '@api';
import {
  chartResamples,
  DomainChangeEvent,
  RangeHorizon,
  RESAMPLE_DEFAULT,
  ResampleIntervals,
  TimeSeriesItem,
} from '@chart';
import { Dispatch, SetStateAction, useCallback, useRef, useState } from 'react';

import {
  updateAllPanes,
  updateChartPane,
} from '../../utils/chart-utils/chart-utils';
import type {
  TerminalChartAssetData,
  TerminalPaneData,
} from '../../utils/chart-utils/types';
import {
  allowedResamplesForHorizon,
  createNewResampleData,
  getResampleCacheKey,
  getResampleForHorizon,
  shouldDisable1DHorizon,
} from '../../utils/resample-utils/resample-utils';
import { ChartState } from '../use-chart-state/useChartState';
import { GetTimeSeriesProps } from '../use-turbo-chart/useTurboChart';

export interface UseResampleProps {
  chartData: TerminalChartAssetData[];
  hasSinglePriceEntity: boolean;
  setHasError: Dispatch<SetStateAction<boolean>>;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  chartState: ChartState;
  handleEmptyChart: (text: string) => void;
  updateChartState: (partial: Partial<ChartState>) => void;
  getTimeSeries: (
    params: GetTimeSeriesProps
  ) => Promise<TimeSeriesItem[] | undefined>;
}

export type UseResampleReturn = ReturnType<typeof useResample>;

export const useResample = ({
  chartState,
  chartData,
  hasSinglePriceEntity,
  setHasError,
  handleEmptyChart,
  updateChartState,
  getTimeSeries,
  setIsLoading,
}: UseResampleProps) => {
  const [emptyResamples, setEmptyResamples] = useState<ResampleIntervals[]>([]);
  const resamplesData = useRef<
    Record<ReturnType<typeof getResampleCacheKey>, TerminalChartAssetData>
  >({});
  const emptyAssetRef = useRef<Entity>(undefined);

  const {
    chartPanes,
    horizon: selectedRange,
    resample: selectedResample,
  } = chartState;
  const disable1DHorizon = shouldDisable1DHorizon({
    chartData,
    emptyResamples,
    resample: selectedResample,
    hasSinglePriceEntity,
  });
  const enabledResamples = chartResamples.filter(
    item => !emptyResamples.includes(item)
  );

  const getCachedResampleData = (
    chartData: TerminalChartAssetData[],
    resample: ResampleIntervals
  ) =>
    chartData.map(item => ({
      ...resamplesData.current[getResampleCacheKey(item, resample)],
      //handle case when cache value has a different color/isHidden/seriesType from the current one in chartData
      lineColorToken: item.lineColorToken,
      isHidden: item.isHidden,
      seriesType: item.seriesType,
    }));

  const updateChartData = (
    chartPanes: TerminalPaneData[],
    resample = selectedResample
  ) => {
    chartPanes.forEach(pane => {
      updateResampleCache(pane.chartAssetData, resample);
    });
  };

  const updateResampleCache = (
    data: TerminalChartAssetData[],
    resample = selectedResample
  ) => {
    data.forEach(item => {
      resamplesData.current = {
        ...resamplesData.current,
        [getResampleCacheKey(item, resample)]: item,
      };
    });
  };

  //useCallback is used as this function will be used as a deps in useEffect
  const onDomainChange = useCallback(
    (event: Event) => {
      const { domain, isSameDomain } = (event as DomainChangeEvent).detail;
      updateChartState({
        ...(!isSameDomain && { horizon: null, chartChanged: true }),
        domain,
      });
    },
    [chartState]
  );

  const changeRange = async (range: RangeHorizon) => {
    const isSameSelected = selectedRange && selectedRange === range;

    if (isSameSelected) {
      return;
    }

    const resample = getResampleForHorizon({
      selectedResample,
      hasSinglePriceEntity,
      range,
      emptyResamples,
    });

    if (resample === null) {
      return;
    }

    changeResample(resample, range);
  };

  const changeResample = async (
    resample: ResampleIntervals,
    range = selectedRange
  ) => {
    const data = await fetchResampleIfNeeded(chartData, resample);
    const isSameData = data === chartData;
    // Custom domain and horizon are mutually exclusive in URL state.
    // Selecting a horizon must drop any prior pan/zoom domain or reload
    // re-applies the stale window over the horizon-derived range.
    const isHorizonChange = range !== selectedRange;

    if (emptyAssetRef.current) {
      const isResampleAllowed =
        !range || allowedResamplesForHorizon[range].includes(selectedResample);
      const nextResample = isResampleAllowed
        ? selectedResample
        : RESAMPLE_DEFAULT;
      handleEmptyChart(`${resample} ${emptyAssetRef.current.name}`);
      setEmptyResamples([...emptyResamples, resample]);

      if (!isResampleAllowed && range === RangeHorizon.OneDay) {
        return;
      }

      const newChartPanes = updateChartPane(
        chartPanes,
        getCachedResampleData(chartData, nextResample)
      );
      updateChartState({
        resample: selectedResample,
        horizon: range,
        ...(isHorizonChange && { domain: null }),
        chartPanes: newChartPanes,
        chartChanged: false,
      });
    } else if (!isSameData) {
      const newChartPanes = updateAllPanes(data, chartPanes);
      updateChartData(newChartPanes, resample);
      updateChartState({
        chartPanes: newChartPanes,
        resample,
        horizon: isHorizonChange ? range : null,
        ...(isHorizonChange && { domain: null }),
        chartChanged: true,
      });
    }

    setIsLoading(false);
  };

  const fetchResampleIfNeeded = async (
    chartData: TerminalChartAssetData[],
    resample: ResampleIntervals
  ) => {
    const unCachedChartData = chartData.filter(
      item => !resamplesData.current[getResampleCacheKey(item, resample)]
    );

    if (unCachedChartData.length) {
      try {
        setHasError(false);
        const prices = await Promise.all(
          unCachedChartData.map(c =>
            getTimeSeries({
              latestAsset: c.entity,
              resolution: resample,
            })
          )
        );

        const emptyAssetIndex = prices.findIndex(item => !item?.length);
        const emptyAsset = unCachedChartData[emptyAssetIndex];
        const data = emptyAsset
          ? chartData
          : createNewResampleData({
              unCachedChartData,
              chartData,
              resample,
              prices: prices as TimeSeriesItem[][],
              resamplesData: resamplesData.current,
            });
        emptyAssetRef.current = emptyAsset?.entity;
        return data;
      } catch {
        emptyAssetRef.current = undefined;
        setHasError(true);
        return chartData;
      }
    }

    emptyAssetRef.current = undefined;
    return getCachedResampleData(chartData, resample);
  };

  return {
    selectedRange,
    changeRange,
    updateChartData,
    onDomainChange,
    disable1DHorizon,
    resample: {
      resamplesData,
      selectedResample,
      enabledResamples,
      emptyResamples,
      changeResample,
      fetchResampleIfNeeded,
      setEmptyResamples,
    },
  };
};
